import React, { useEffect, useState } from "react";
import { useCallback } from "react";
import { Auth } from "@aws-amplify/auth";
import { CognitoUser } from "amazon-cognito-identity-js";
import { QRCodeSVG } from "qrcode.react";
import {
  EuiFieldText,
  EuiButton,
  EuiFormRow,
  EuiText,
  EuiSpacer,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiButtonEmpty,
  EuiCopy,
  EuiEmptyPrompt,
  EuiLoadingChart,
  EuiPanel,
  EuiLink,
} from "@inscopix/ideas-eui";
import { captureException } from "@sentry/react";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { useFlyoutContext } from "providers/FlyoutProvider/FlyoutProvider";

interface MFASetupFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export const MFASetupForm = ({ onSuccess, onCancel }: MFASetupFormProps) => {
  const { setFlyout } = useFlyoutContext();

  const [setupUri, setSetupUri] = useState<string | null>(null);
  const [sharedSecret, setSharedSecret] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<"initializing" | "failed" | "setup">(
    "initializing",
  );

  const handleStartSetup = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    // generate TOTP Setup URI
    try {
      // Get current authenticated user
      // Our version of AWS amplify types this as any, have to force to CognitoUser to avoid TypeScript warning
      const user = (await Auth.currentAuthenticatedUser()) as CognitoUser;

      const secretCode = await Auth.setupTOTP(user);

      const issuer = "IDEAS";
      const email = user.getUsername();
      const encodedEmail = encodeURIComponent(email);
      const uri = `otpauth://totp/${issuer}:${encodedEmail}?secret=${secretCode}&issuer=${issuer}`;

      setSetupUri(uri);
      setSharedSecret(secretCode);
      setStep("setup");
    } catch (err) {
      setStep("failed");
      captureException(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // setup when component mounts if state is initializing
  useEffect(() => {
    if (step === "initializing") {
      void handleStartSetup();
    }
  }, [handleStartSetup, step]);

  // verify the TOTP code from authenticator app
  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();

    if (totpCode.length !== 6) {
      setError("TOTP code must be 6 digits");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const user = (await Auth.currentAuthenticatedUser()) as CognitoUser;

      await Auth.verifyTotpToken(user, totpCode);

      await Auth.setPreferredMFA(user, "SOFTWARE_TOKEN_MFA");

      setSetupUri(null);
      setSharedSecret(null);
      setTotpCode("");

      onSuccess?.();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to verify TOTP code";
      setError(errorMessage);
      captureException(err);
      addUtilityToastFailure("There was an error verifying your TOTP code");
      setStep("setup");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <EuiText size="s">
        <h2>Set Up Two-Factor Authentication</h2>
      </EuiText>

      <>
        {step === "failed" && (
          <EuiCallOut
            color="danger"
            iconType="warning"
            title={"Failed to initialize MFA setup"}
          >
            <EuiText>
              <p>
                There was an error loading user information to set up MFA. Retry
                again using the button below or{" "}
                <EuiLink onClick={() => setFlyout("contactSupport")}>
                  contact support
                </EuiLink>{" "}
                if the issue persists.
              </p>
              <EuiButton
                onClick={() => {
                  void handleStartSetup();
                }}
                fill
              >
                Retry
              </EuiButton>
            </EuiText>
          </EuiCallOut>
        )}
        {step === "initializing" && (
          <EuiEmptyPrompt
            icon={<EuiLoadingChart aria-label="Loading..." size="xl" />}
            title={<h2>Loading...</h2>}
          />
        )}

        {step === "setup" && setupUri != null && (
          <div>
            <EuiText>
              <p>
                Use an authenticator app to scan this QR code or manually enter
                the secret key below into your app. Then, enter the 6-digit code
                generated by your app to verify and enable two-factor
                authentication.
              </p>
            </EuiText>

            <EuiSpacer size="m" />

            <div style={{ textAlign: "center", marginBottom: "2rem" }}>
              <QRCodeSVG value={setupUri} size={256} level="H" />
            </div>

            {sharedSecret != null && (
              <>
                <EuiPanel hasBorder={true} paddingSize="s">
                  <EuiSpacer size="s" />
                  <EuiFormRow label="Secret Key">
                    <EuiFlexGroup gutterSize="s" alignItems="center">
                      <EuiFlexItem grow={false}>
                        <code
                          style={{
                            padding: "0.5rem",
                            borderRadius: "4px",
                            display: "inline-block",
                          }}
                        >
                          {sharedSecret}
                        </code>
                      </EuiFlexItem>
                      <EuiFlexItem grow={false}>
                        <EuiCopy textToCopy={sharedSecret}>
                          {(copy) => (
                            <EuiButtonEmpty size="xs" onClick={copy}>
                              Copy
                            </EuiButtonEmpty>
                          )}
                        </EuiCopy>
                      </EuiFlexItem>
                    </EuiFlexGroup>
                  </EuiFormRow>
                </EuiPanel>
                <EuiSpacer size="m" />
              </>
            )}

            <form
              onSubmit={(e) => {
                void handleVerifyCode(e);
              }}
            >
              <EuiFormRow label="Enter 6-digit code from authenticator app">
                <EuiFieldText
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  pattern="[0-9]{6}"
                  value={totpCode}
                  onChange={(e) =>
                    setTotpCode(e.target.value.replace(/\D/g, ""))
                  }
                  required
                />
              </EuiFormRow>

              {error != null && (
                <>
                  <EuiSpacer size="m" />
                  <EuiCallOut
                    title="Error"
                    color="danger"
                    iconType="alert"
                    onDismiss={() => setError(null)}
                  >
                    {error}
                  </EuiCallOut>
                </>
              )}

              <EuiSpacer size="m" />

              <EuiFlexGroup justifyContent="flexEnd">
                <EuiFlexItem grow={false}>
                  <EuiButtonEmpty
                    onClick={() => {
                      setSetupUri(null);
                      setSharedSecret(null);
                      setTotpCode("");
                      onCancel();
                    }}
                  >
                    Cancel
                  </EuiButtonEmpty>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiButton
                    type="submit"
                    isLoading={isLoading}
                    disabled={totpCode.length !== 6}
                    fill
                  >
                    Verify & Enable
                  </EuiButton>
                </EuiFlexItem>
              </EuiFlexGroup>
            </form>
          </div>
        )}
      </>
    </>
  );
};
